export {
	DEFAULT_OFFICE_TIMINGS,
	EMPTY_SCHEDULE_ERROR_MESSAGE,
	IVR_OFFICE_HOURS_ERROR_MESSAGE,
	OFFICE_DAYS,
	POP_UP_CONFIRMATION_MESSAGE,
	WMA_WEBLINK_ERROR_MESSAGE,
	WMA_WEBLINK_SUCCESS_MESSAGE,
} from '@/config/constants'

/** Settings section route param / sidebar ids */
export const SETTINGS_SECTION_IDS = {
	ORGANIZATION: 'organization',
	GENERAL_INFORMATION: 'generalInformation',
	OFFICE_HOURS: 'officeHours',
	SECONDARY_VOICEMAIL: 'secondaryVoicemail',
	EHR_CONFIGURATION: 'ehrConfiguration',
}

export const SETTINGS_SECTION_LABELS = {
	[SETTINGS_SECTION_IDS.GENERAL_INFORMATION]: 'General Information',
	[SETTINGS_SECTION_IDS.OFFICE_HOURS]: 'Office Hours',
	[SETTINGS_SECTION_IDS.SECONDARY_VOICEMAIL]: 'Secondary Voicemail',
	[SETTINGS_SECTION_IDS.EHR_CONFIGURATION]: 'EHR Configuration',
	[SETTINGS_SECTION_IDS.ORGANIZATION]: 'Organization Profile',
}

export const SETTINGS_NAV_ITEMS = [
	{
		id: SETTINGS_SECTION_IDS.GENERAL_INFORMATION,
		label:
			SETTINGS_SECTION_LABELS[SETTINGS_SECTION_IDS.GENERAL_INFORMATION],
	},
	{
		id: SETTINGS_SECTION_IDS.OFFICE_HOURS,
		label: SETTINGS_SECTION_LABELS[SETTINGS_SECTION_IDS.OFFICE_HOURS],
	},
	{
		id: SETTINGS_SECTION_IDS.SECONDARY_VOICEMAIL,
		label:
			SETTINGS_SECTION_LABELS[SETTINGS_SECTION_IDS.SECONDARY_VOICEMAIL],
	},
	{
		id: SETTINGS_SECTION_IDS.EHR_CONFIGURATION,
		label: SETTINGS_SECTION_LABELS[SETTINGS_SECTION_IDS.EHR_CONFIGURATION],
	},
]

export const SETTINGS_COMMON = {
	settings: 'Settings',
	settingsNavigationAriaLabel: 'Settings navigation',
	save: 'Save',
	cancel: 'Cancel',
	edit: 'Edit',
	confirm: 'Confirm',
	select: 'Select',
	verified: 'Verified',
	help: 'Help',
	password: 'Password',
	hidePassword: 'Hide Password',
	showPassword: 'Show Password',
}

export const SETTINGS_PLACEHOLDERS = {
	phoneNumber: 'Phone number',
	faxNumber: 'Fax number',
	addressLine1: 'Address line 1',
	addressLine2: 'Address line 2',
	city: 'City',
	stateOrProvince: 'State or Province',
	country: 'Country',
	postalCode: 'Postal code',
	fallbackNotificationEmail: 'Fallback notification email',
	notificationPhoneNumber: 'Notification phone number',
	password: 'Password',
	username: 'Username',
	destinationId: 'Destination ID',
	applicationId: 'Application ID',
	facilityId: 'Facility ID',
}

export const SETTINGS_ARIA = {
	clearOutboundCallerId: 'Clear outbound caller ID',
	clearOfficePhoneNumber: 'Clear office phone number',
	clearOfficeFaxNumber: 'Clear office fax number',
	clearAddressLine1: 'Clear address line 1',
	clearAddressLine2: 'Clear address line 2',
	clearCity: 'Clear city',
	clearStateOrProvince: 'Clear state or province',
	clearCountry: 'Clear country',
	clearPostalCode: 'Clear postal code',
	clearFallbackNotificationEmail: 'Clear fallback notification email',
	clearFallbackNotificationPhoneNumber:
		'Clear fallback notification phone number',
	clearUsername: 'Clear username',
	clearDestinationId: 'Clear destination ID',
	clearApplicationId: 'Clear application ID',
	clearFacilityId: 'Clear facility ID',
	clearProfileFriendlyName: 'Clear profile friendly name',
	copySettings: 'Copy settings',
}

export const SETTINGS_VALIDATION = {
	passcodeDigitsOnly: 'Passcode can only contain digits.',
	passcodeMinLength: 'Passcode must be at least 6 digits long.',
	passcodeNoSequential:
		'Passcode cannot contain 3 or more sequential digits.',
	passcodeNoRepeating: 'Passcode cannot contain 3 or more repeating digits.',
	passcodeRequired: 'Passcode is required.',
	phoneTenDigits: 'Phone number has to be a valid 10 digit number.',
	phoneSameAsTwilio:
		'Phone number cannot be the same as your Twilio phone number.',
	phoneSameAsTwilioOrOffice:
		'Phone number cannot be the same as Twilio Phone or Office Phone.',
	officePhoneRequired: 'Office phone number is required.',
	outboundCallerIdRequired: 'Outbound Caller Id is required.',
	officeFaxRequired: 'Office fax number is required.',
	faxTenDigits: 'Fax number has to be a valid 10 digit number.',
	emailInvalid: 'Please enter a valid email address.',
	emailDuplicate: 'Duplicate email address. Please enter a unique email.',
	emailNoCapitalLetters: 'Email addresses cannot contain capital letters.',
	duplicateEmails: duplicates =>
		`Duplicate emails found: ${duplicates.join(', ')}.`,
	invalidTime: 'Please enter a valid time.',
	noteTypeRequired: 'At least one note type must be selected.',
}

export const GENERAL_INFORMATION_STRINGS = {
	title: 'General Information',
	subtitle: 'Basic practice details and contact information.',
	practiceCardTitle: 'Practice Information',
	addressCardTitle: 'Address',
	fallbackCardTitle: 'Fallback Information',
	fields: {
		practiceName: 'Practice Name',
		webDisplayName: 'Web Display Name',
		wmaWeblink: 'WMA Weblink',
		forwardingNumber: 'Forwarding Number',
		outboundCallerId: 'Outbound Caller ID',
		officePhoneNumber: 'Office Phone Number',
		officeFaxNumber: 'Office Fax Number',
		timezone: 'Timezone',
		addressLine1: 'Address Line 1',
		addressLine2Optional: 'Address Line 2 - Optional',
		city: 'City',
		stateProvince: 'State/Province',
		country: 'Country',
		postalCode: 'Postal Code',
		fallbackNotificationEmail: 'Fallback Notification Email',
		fallbackNotificationPhoneNumber: 'Fallback Notification Phone Number',
	},
	tooltips: {
		copyWmaWeblink: 'Copy WMA Weblink',
	},
	table: {
		ivr: 'IVR',
		ivrAssignment: 'IVR Assignment',
		fallbackEmail: 'Fallback Email',
		fallbackPhoneNumber: 'Fallback Phone Number',
		selectSingleForwardingNumber: 'Select Single Forwarding Number',
	},
	forwardingNumberWillBeRemoved: 'This number will be removed',
	callerId: {
		verifyButton: 'Verify Caller ID',
		helpText:
			'This number must be verified to be used for outbound caller ID. If the number is not verified, the forwarding number will show as your outbound caller ID.',
	},
	selectTimezone: 'Select a timezone',
	organizationCard: {
		title: 'Organization',
		manageButton: 'Manage Organization',
	},
}

export const OFFICE_HOURS_STRINGS = {
	title: 'Office Hours',
	subtitle: 'Manage Office Opening Hours and Weekly Schedule',
	table: {
		officeHoursGroup: 'OFFICE HOURS',
		lunchHoursGroup: 'LUNCH HOURS',
		day: 'DAY',
		openClosed: 'OPEN/CLOSED',
		start: 'START',
		end: 'END',
	},
	toggle: {
		open: 'Open',
		closed: 'Closed',
	},
	validation: {
		officeTimesRequired: 'Office start and end times are required.',
		lunchWithinOfficeHours: 'Lunch time must be within office hours.',
		officeTimesSame: 'Office start and end times cannot be the same.',
		lunchTimesSame: 'Lunch start and end times cannot be the same.',
	},
	saveSuccess: 'Office hours saved successfully.',
}

export const SECONDARY_VOICEMAIL_STRINGS = {
	title: 'Secondary Voicemail',
	subtitle: 'Secondary Voicemail Configuration.',
	fields: {
		voicemailRetrievalNumber: 'Voicemail Retrieval Number',
		mailboxNumber: 'Mailbox Number',
		emailNotifications: 'Email Notifications',
	},
	placeholders: {
		addEmail: 'Add Email...',
		addEmails: 'Add Emails...',
	},
	actions: {
		help: 'Help',
		copySettings: 'Copy Settings',
	},
	copySettingsValidationError:
		'Please fill in the email notifications and enter a valid passcode to copy settings.',
	copySuccess: 'Secondary Voicemail data copied to clipboard.',
	copyError: 'Failed to copy Secondary Voicemail data.',
	instructions: {
		title: 'Secondary Voicemail Instructions',
		intro:
			'The Secondary Voicemail system ensures that all messages are captured should there be a momentary service interruption with our partners, platform, or your IVR. When a message is left, an email with instructions to retrieve the message is sent to your Email Notification recipients. The configuration and instructions for your mailbox number, passcode, and email recipients are defined in your Office Manager Dashboard, Settings under Secondary Voicemail.',
		importantNotesHeading: 'Important Notes:',
		noteMultipleUsers:
			'- Multiple users can retrieve messages at the same time.',
		noteNoPasscodeSupport: '- Clarus support does not have your passcode.',
		noteNotInDashboard:
			'- Messages will not appear in your Office Dashboard or Clarus App at this time.',
		configurationHeading:
			'Configuration of your Secondary Voicemail service:',
		configurationBody:
			'Update your Email Notification recipients to a list of important users who may need to be notified if a message is received, such as your phone staff or providers. This service supports more than one recipient. The initial email is copied from your fallback notification email. Click the Card icon to copy them to your clipboard for printing, share with your providers, or save them to shared contacts, as they might not be available during an interruption.',
		retrieveHeading: 'To retrieve your messages:',
		stepDial: retrievalNumber =>
			`1. Dial the voicemail retrieval number: ${retrievalNumber}.`,
		stepMailbox: mailboxNumber =>
			`2. Enter your ten-digit mailbox number: ${mailboxNumber}.`,
		stepPasscode: passcode => `3. Enter your passcode: ${passcode}.`,
		stepReplay:
			'4. You can play and re-play each message until you have captured all the details.',
		stepNoRewind:
			'5. Once you play the next message, you will not be able to listen to the previous or older messages.',
	},
}

export const EHR_CONFIGURATION_STRINGS = {
	title: 'EHR Configuration',
	subtitle: 'Configure your EHR integration and manage system settings.',
	configCardTitle: 'Configuration',
	noteTypesCardTitle: 'Configure Note Types',
	fields: {
		provider: 'Provider',
		username: 'Username',
		password: 'Password',
		destinationId: 'Destination ID',
		applicationId: 'Application ID',
		facilityId: 'Facility ID',
	},
	noteTypes: {
		loadError: 'Unable to load note types. Please try again later.',
		empty: 'No note types are available for this EHR integration.',
	},
	notePreview: {
		callReceived: 'Call Received:',
		message: 'Message:',
		notes: 'Notes:',
	},
}

export const ORGANIZATION_STRINGS = {
	title: 'Organization Profile',
	subtitle:
		'Information about your business entity. Used by Twilio to issue an attestation on outbound calls.',
	adminsCard: {
		title: 'Org Admins',
		viewOnlyBadge: 'View only · managed by Clarus Admin',
		description:
			'These users are registered with Twilio as the Primary and Secondary Authorized Representatives on the SCP. To make any changes, please contact your Clarus account manager.',
	},
	adminRole: {
		primary: 'Primary',
		secondaryOptional: 'Secondary - optional',
		representative: 'Representative',
	},
	actions: {
		saveDraft: 'Save Draft',
		submitForApproval: 'Submit for Approval',
	},
	businessIdentityCardTitle: 'Business Identity',
	headquartersAddressTitle: 'Headquarters Address',
	fields: {
		profileFriendlyName: 'Profile Friendly Name',
		legalBusinessName: 'Legal Business Name',
		businessIdentity: 'Business Identity',
		businessType: 'Business Type',
		businessIndustry: 'Business Industry',
		placeOfIncorporation: 'Place of Incorporation',
		businessRegistrationIdType: 'Business Registration ID Type',
		businessRegistrationIdEin: 'Business Registration ID (EIN)',
		businessRegionsOfOperation: 'Business Regions of Operation',
		businessWebsite: 'Business Website',
		addressLine1: 'Address Line 1',
		addressLine2: 'Address Line 2',
		city: 'City',
		stateProvince: 'State / Province',
		postalCode: 'Postal Code',
		country: 'Country',
	},
	saveDraftSuccess: 'Draft saved.',
	submitSuccess: 'Organization profile submitted for approval.',
}

export const VERIFY_CALLER_ID_STEPS = {
	SETUP: 'setup',
	CALLING: 'calling',
	TIMEOUT: 'timeout',
	SUCCESS: 'success',
}

/** Seconds before the verification call is treated as timed out. */
export const VERIFY_CALLER_ID_TIMEOUT_SECONDS = 120

/** Milliseconds between verification status poll requests. */
export const VERIFY_CALLER_ID_STATUS_POLL_INTERVAL_MS = 10000

export const VERIFY_CALLER_ID_STRINGS = {
	title: 'Verify Caller ID',
	intro:
		"We'll call your office number to confirm Clarus has authority to mask outbound calls as that number.",
	fields: {
		officeNumberLabel: "We'll call this office number",
		extensionCodeLabel: 'Extension Code',
	},
	placeholders: {
		extensionCode: 'Extension code',
	},
	extensionHelp:
		"Enter the IVR navigation sequence. Use 'w' for a 1-second pause and digits 0–9, *, and #.",
	extensionHelpExample: 'Example: w2w3 waits, presses 2, waits, presses 3.',
	callout: {
		title: 'Important — someone must be at this phone',
		body:
			"You'll be asked to enter a 6-digit code. We'll show the code on the next screen. Customer Success can be on a parallel call to help, but the code must be entered at the office phone.",
	},
	actions: {
		placingCall: 'Placing call...',
		placeVerificationCall: 'Place Verification Call',
		done: 'Done',
		close: 'Close',
	},
	timerLabel: 'Time elapsed',
	pinLabel: 'PIN — ENTER THE DIGITS ON YOUR PHONE.',
	pinAriaLabel: 'Verification PIN',
	instruction:
		'Please enter this 6-digit PIN on your phone keypad to complete verification.',
	connectionError: 'Unable to connect please try again.',
	status: {
		connecting: 'Connecting to office phone...',
		timeout: 'Call timed out',
		verified: 'Caller ID Verified',
	},
	success: {
		intro: 'Your office number',
		outro:
			'is now verified. Outbound calls from Clarus will display this number.',
	},
	validation: {
		invalidExtensionCode:
			'Invalid extension code. Please enter the correct code and place the call.',
	},
	errors: {
		placeCallFailed: 'Failed to place verification call.',
	},
}
